import { useEffect, useState } from 'react';
import { fetchOrderAccessSummary } from './smartLockApi';

/**
 * Batched, cached lookup of "does this order have a mapped smart lock, and
 * what's its access status" for dispatch tiles. Many tiles mount at once; we
 * coalesce their ids into one POST and cache for a short TTL so scrolling and
 * re-renders never fan out into per-tile requests.
 */

const TTL_MS = 60 * 1000;
const BATCH_DELAY_MS = 80;
const MAX_BATCH = 200;

const cache = new Map(); // orderId -> { at, value }
const listeners = new Map(); // orderId -> Set<fn>
let pending = new Set();
let timer = null;
let integrationDisabled = false;

const notify = (orderId) => {
  const fns = listeners.get(orderId);
  if (!fns) return;
  const entry = cache.get(orderId);
  fns.forEach((fn) => fn(entry ? entry.value : null));
};

const flush = async () => {
  timer = null;
  const ids = Array.from(pending).slice(0, MAX_BATCH);
  pending = new Set(Array.from(pending).slice(MAX_BATCH));
  if (!ids.length) return;
  try {
    const result = await fetchOrderAccessSummary(ids);
    const now = Date.now();
    ids.forEach((id) => {
      cache.set(id, { at: now, value: result[id] || { mapped: false } });
      notify(id);
    });
  } catch (err) {
    if (err?.response?.status === 503) integrationDisabled = true;
    const now = Date.now();
    ids.forEach((id) => {
      cache.set(id, { at: now, value: { mapped: false, error: true } });
      notify(id);
    });
  }
  if (pending.size) timer = setTimeout(flush, BATCH_DELAY_MS);
};

const schedule = (orderId) => {
  if (integrationDisabled) return;
  pending.add(orderId);
  if (!timer) timer = setTimeout(flush, BATCH_DELAY_MS);
};

/** Drop the cached entry so the next mount/refresh refetches (call after provision/revoke). */
export const invalidateSmartLockSummary = (orderId) => {
  if (!orderId) return;
  cache.delete(orderId);
  schedule(orderId);
};

export default function useSmartLockSummary(orderId) {
  const [value, setValue] = useState(() => {
    const entry = orderId ? cache.get(orderId) : null;
    return entry && Date.now() - entry.at < TTL_MS ? entry.value : null;
  });

  useEffect(() => {
    if (!orderId) return undefined;
    const id = String(orderId);
    if (!listeners.has(id)) listeners.set(id, new Set());
    listeners.get(id).add(setValue);
    const entry = cache.get(id);
    if (entry && Date.now() - entry.at < TTL_MS) setValue(entry.value);
    else schedule(id);
    return () => {
      const set = listeners.get(id);
      if (set) {
        set.delete(setValue);
        if (!set.size) listeners.delete(id);
      }
    };
  }, [orderId]);

  return value; // null = unknown yet
}
